import { type FunctionComponent, useState } from "react";
import { graphql, useFragment } from "react-relay";

import type { Props as ArticleSpeechInlineWidgetGenericButtonProps } from "scmp-app/components/article/article-speech/article-speech-inline-widget/variants/generic/button";
import type { VariantProps } from "scmp-app/components/article/article-speech/article-speech-inline-widget/variants/types";
import { getHasArticleSpeech } from "scmp-app/components/article/article-speech/utilities";
import type { genericArticleSpeechInlineWidgetGenericArticle$key } from "scmp-app/queries/__generated__/genericArticleSpeechInlineWidgetGenericArticle.graphql";

import { ArticleSpeechInlineWidgetGenericButton } from "./button";
import { EmptyButton } from "./empty-button";
import {
  useEnableGenericInlineWidgetByUserSubscription,
  useShowGenericInlineWidgetByArticle,
} from "./hooks";
import { Container, Wrapper } from "./styles";
import { ArticleSpeechWidgetToolTip } from "./tool-tip";

export type Props = Omit<
  ArticleSpeechInlineWidgetGenericButtonProps,
  "entityId" | "reference" | "setIsTooltipActive"
> &
  VariantProps & {
    reference: genericArticleSpeechInlineWidgetGenericArticle$key;
  };

export const ArticleSpeechInlineWidgetGeneric: FunctionComponent<Props> = ({
  className,
  reference,
  ...props
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleSpeechInlineWidgetGenericArticle on Article {
        entityId
        ...buttonArticleSpeechInlineWidgetGenericButtonArticle
        ...hooksShowGenericInlineWidgetByArticleArticle
        ...toolTipArticleSpeechWidgetToolTipArticle
        ...utilitiesGetHasArticleSpeechArticle
      }
    `,
    reference,
  );
  const { isForceHideSCMPArticleSpeechWidget } = useShowGenericInlineWidgetByArticle(article);
  const { isWidgetEnabledByUser } = useEnableGenericInlineWidgetByUserSubscription();
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const hasSpeech = getHasArticleSpeech(article);

  const handleRenderArticleSpeechWidget = () => {
    if (!hasSpeech) return null;

    // Force-hide takes precedence over the placeholder: a force-hidden widget renders
    // nothing from the start, so SSR and CSR agree without flashing the placeholder.
    if (isForceHideSCMPArticleSpeechWidget) return null;

    // `undefined` = config not loaded yet; `isWidgetEnabledByUser` is client-only.
    // Keep the placeholder until both are known to avoid layout shift.
    if (isForceHideSCMPArticleSpeechWidget === undefined || isWidgetEnabledByUser === undefined)
      return <EmptyButton className={className} />;

    return (
      <Container className={className}>
        <ArticleSpeechWidgetToolTip
          isTooltipActive={isTooltipActive}
          reference={article}
          setIsTooltipActive={setIsTooltipActive}
        >
          <ArticleSpeechInlineWidgetGenericButton
            {...props}
            entityId={article.entityId}
            reference={article}
            setIsTooltipActive={setIsTooltipActive}
          />
        </ArticleSpeechWidgetToolTip>
      </Container>
    );
  };

  const content = handleRenderArticleSpeechWidget();
  if (!content) return null;

  return <Wrapper $isMinify={props.isMinify}>{content}</Wrapper>;
};

ArticleSpeechInlineWidgetGeneric.displayName = "ArticleSpeechInlineWidgetGeneric";
